/**
 * Componente EmptyState - Estado Vacío Reutilizable
 * Muestra un estado vacío con icono, mensaje y acción opcional
 * ESTÁNDAR: Usar en toda la aplicación para estados vacíos
 */

import React from 'react';
import { Box, Typography, Button, ButtonProps, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface EmptyStateProps {
  /**
   * Icono a mostrar (componente de MUI Icons)
   */
  icon: React.ReactElement;
  
  /**
   * Título principal
   */
  title: string;
  
  /**
   * Descripción o mensaje secundario
   */
  description?: string;
  
  /**
   * Texto del botón de acción (opcional)
   */
  actionLabel?: string;
  
  /**
   * Función a ejecutar al hacer clic en el botón
   */
  onAction?: () => void;
  
  /**
   * Props adicionales para el botón
   */
  actionButtonProps?: ButtonProps;
  
  /**
   * Estilos personalizados
   */
  sx?: SxProps<Theme>;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  actionButtonProps,
  sx,
}) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 2,
        ...sx,
      }}
    >
      <Box
        sx={{
          color: theme.palette.text.disabled,
          mb: 3,
          '& svg': {
            fontSize: 64,
          },
        }}
      >
        {icon}
      </Box>
      
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      
      {description && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: 400,
            mb: actionLabel ? 3 : 0,
          }}
        >
          {description}
        </Typography>
      )}
      
      {actionLabel && onAction && (
        <Button
          variant="contained"
          onClick={onAction}
          {...actionButtonProps}
          sx={{
            mt: 2,
            ...actionButtonProps?.sx,
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
};

